/**
 * RFC 9457 problem+json error handling for the pipeline orchestrator service.
 * Replaces the legacy `{error: string}` shape from authenticatedFetch.
 */

export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance: string;
  request_id: string;
  errors?: Array<{ pointer: string; code: string; message: string }>;
  retry_after_seconds?: number;
  pipeline_run_id?: string;
}

export class PipelineApiError extends Error {
  constructor(
    public status: number,
    public problem: ProblemDetails,
    public response: Response
  ) {
    super(problem.title);
    this.name = 'PipelineApiError';
  }

  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const e of this.problem.errors ?? []) {
      const key = e.pointer.replace(/^\//, '').replace(/\//g, '.');
      if (!(key in out)) out[key] = e.message;
    }
    return out;
  }
}

export async function unwrap<T>(res: Response): Promise<T> {
  if (res.ok) {
    if (res.status === 204) return undefined as T;
    return res.json() as Promise<T>;
  }
  let problem: ProblemDetails;
  try {
    problem = await res.json();
  } catch {
    problem = {
      type: 'about:blank',
      title: res.statusText || 'Request failed',
      status: res.status,
      instance: '',
      request_id: ''
    };
  }
  throw new PipelineApiError(res.status, problem, res);
}
